import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, friendlyError } from "@/lib/api";
import { VoiceInputButton } from "@/components/app/VoiceInputButton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, ArrowRight, AlertCircle } from "lucide-react";

const FIELDS = [
  ["product_or_service", "Product / service", "e.g. Custom food containers"],
  ["quantity", "Quantity", "e.g. 2,000 units"],
  ["specifications", "Specifications", "Dimensions, materials, colors…"],
  ["quality_requirements", "Quality requirements", "e.g. Food-grade"],
  ["destination", "Destination", "e.g. Kathmandu, Nepal"],
  ["delivery_deadline", "Delivery deadline", "e.g. Within 25 days"],
  ["budget", "Budget", "e.g. 6,000"],
  ["currency", "Currency", "e.g. USD"],
  ["shipping_requirements", "Shipping", "e.g. CIF, air freight"],
  ["certifications", "Certifications", "e.g. FDA, ISO 9001"],
  ["customization", "Customization", "e.g. Branded print"],
  ["special_requirements", "Special requirements", "Anything else"],
];

export default function NewRequest() {
  const [text, setText] = useState(sessionStorage.getItem("gb_pending_request") || "");
  const [extracting, setExtracting] = useState(false);
  const [reqs, setReqs] = useState(null);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  useEffect(() => { sessionStorage.removeItem("gb_pending_request"); }, []);

  const extract = async () => {
    if (!text.trim() || extracting) return;
    setExtracting(true);
    try {
      const { data } = await api.post("/sourcing/extract", { text });
      setReqs(data.requirements);
      toast.success("AI extraction complete. Review and edit before saving.");
    } catch (err) { toast.error(friendlyError(err)); }
    finally { setExtracting(false); }
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/sourcing/requests", { original_text: text, requirements: reqs });
      toast.success("Sourcing request saved");
      nav(`/buyer/requests/${data.id}`);
    } catch (err) { toast.error(friendlyError(err)); }
    finally { setSaving(false); }
  };

  const updateField = (k) => (e) => setReqs((r) => ({ ...r, [k]: e.target.value }));

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <div className="eyebrow text-[hsl(var(--navy))] opacity-70 mb-2">New sourcing request</div>
        <h1 className="font-display text-3xl md:text-4xl">Describe what you need</h1>
        <p className="text-slate-500 text-sm mt-2">Write it naturally — our AI will extract structured requirements. You review and edit everything before sending to suppliers.</p>
      </div>

      <div className="surface p-5 md:p-6">
        <Label htmlFor="src" className="text-sm">Your sourcing request</Label>
        <div className="flex items-start gap-2 mt-2">
          <Textarea
            id="src"
            data-testid="new-request-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              // Enter inserts a newline (native textarea behaviour).
              // Ctrl/Cmd+Enter submits — explicit shortcut, no accidental sends.
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                if (text.trim() && !extracting) extract();
              }
            }}
            rows={5}
            autoCorrect="on"
            autoCapitalize="sentences"
            spellCheck="true"
            enterKeyHint="enter"
            inputMode="text"
            className="flex-1 resize-y min-h-[140px] max-h-72 leading-relaxed"
            placeholder={"e.g. We need 10,000 anodized aluminum enclosures (120×80×35 mm), surface finish Ra 1.6, delivered to Rotterdam within 8 weeks, budget €18,000 CIF. Preferred suppliers in Germany, Netherlands, or South Korea."}
          />
          <VoiceInputButton onTranscript={setText} existingText={text} testId="new-request-voice-btn"/>
        </div>
        <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
          <div className="text-xs text-slate-500">
            <span className="hidden sm:inline">Press Ctrl/⌘+Enter to extract. </span>
            We never submit anything externally without your approval.
          </div>
          <button onClick={extract} disabled={!text.trim() || extracting} className="btn-gold" data-testid="extract-btn">
            {extracting ? "Analyzing…" : <>Extract with AI <Sparkles size={14}/></>}
          </button>
        </div>
      </div>

      {reqs && (
        <div className="surface p-5 md:p-6" data-testid="requirements-review">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-display text-2xl">Review requirements</h2>
              <p className="text-slate-500 text-sm mt-1">Edit any field. Nothing moves forward until you save.</p>
            </div>
          </div>
          {reqs.missing_information?.length > 0 && (
            <div className="mb-5 rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="missing-info-banner">
              <div className="flex items-start gap-2">
                <AlertCircle size={16} className="mt-0.5"/>
                <div>
                  <div className="font-medium">Missing information</div>
                  <div className="text-amber-800 mt-1">The AI could not infer: {reqs.missing_information.join(", ")}. Fill these in to get better supplier matches.</div>
                </div>
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            {FIELDS.map(([k, label, ph]) => (
              <div key={k} className="space-y-1.5">
                <Label htmlFor={k} className="text-xs tracking-wide uppercase text-slate-500">{label}</Label>
                <Input id={k} value={reqs[k] || ""} onChange={updateField(k)} placeholder={ph} data-testid={`field-${k}`}/>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-end">
            <button onClick={save} disabled={saving} className="btn-gold" data-testid="save-request-btn">
              {saving ? "Saving…" : <>Save &amp; find suppliers <ArrowRight size={14}/></>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
